/**
 * Title-scoped marker for Cloudflare managed-challenge HTML
 * (`<title>Just a moment…</title>`). Prefer matching signals in
 * `CLOUDFLARE_CHALLENGE_SIGNALS` rather than bare marketing copy.
 */
export const CLOUDFLARE_CHALLENGE_MARKER = '<title>Just a moment';

/**
 * Title-scoped marker for Cloudflare WAF / interactive interstitial HTML
 * (`<title>Attention Required! | Cloudflare</title>`). Distinct from managed
 * challenge pages; soft-nav 403 bodies with this title do not reliably include
 * the structural markers below in the truncated log prefix we see in Datadog.
 */
export const CLOUDFLARE_ATTENTION_REQUIRED_MARKER = '<title>Attention Required';

/**
 * Durable markers that appear in Cloudflare bot-challenge HTML.
 * Used for Datadog classification and to avoid treating the challenge as an
 * app permission denial (Page403).
 *
 * Title markers are scoped with `<title>` so ordinary page copy mentioning
 * “Just a moment” cannot false-trigger a hard reload.
 */
export const CLOUDFLARE_CHALLENGE_SIGNALS = [
    CLOUDFLARE_CHALLENGE_MARKER,
    CLOUDFLARE_ATTENTION_REQUIRED_MARKER,
    '/cdn-cgi/challenge-platform/',
    'cf-challenge',
] as const;

/**
 * True when a string contains any Cloudflare challenge signal (title copy or
 * structural path/token). Safe for both raw ErrorResponse bodies and wrapped
 * `ErrorResponse 403 …` log messages.
 */
export const containsCloudflareChallengeSignal = (text: string): boolean =>
    CLOUDFLARE_CHALLENGE_SIGNALS.some((signal) => text.includes(signal));

/**
 * True when a route-error body looks like a Cloudflare bot-challenge document
 * rather than an ArchiPro API / permission message.
 */
export const isCloudflareChallengeBody = (body: unknown): boolean =>
    typeof body === 'string' && containsCloudflareChallengeSignal(body);

/**
 * True when an ErrorResponse is a Cloudflare bot challenge (403 + challenge HTML),
 * not a real app Forbidden response.
 */
export const isCloudflareChallengeResponse = (
    status: number,
    data: unknown,
): boolean => status === 403 && isCloudflareChallengeBody(data);
